/**
 * Superadmin — Billing Overview Page
 * All invoices across all tenants
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MagnifyingGlassIcon, EyeIcon } from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL || 'https://api.shunrindo.com/api/v1';
function getAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
}

const STATUS_COLORS = {
  paid: 'bg-green-100 text-green-800',
  open: 'bg-yellow-100 text-yellow-800',
  void: 'bg-gray-100 text-gray-600',
  draft: 'bg-blue-100 text-blue-800'
};

export default function BillingPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/superadmin/stats`, { headers: getAuthHeaders() }),
      axios.get(`${API}/superadmin/tenants`, { headers: getAuthHeaders(), params: { limit: 100 } })
    ]).then(([statsRes, tenantsRes]) => {
      setStats(statsRes.data);
      setTenants(tenantsRes.data.tenants);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const formatCurrency = (v) => `€${Number(v || 0).toFixed(2)}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900">Billing Overview</h1>

      {/* Summary */}
      {stats?.revenue && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Revenue', value: formatCurrency(stats.revenue.totalRevenue), color: 'text-gray-900' },
            { label: 'This Month', value: formatCurrency(stats.revenue.revenueThisMonth), color: 'text-green-600' },
            { label: 'Pending Invoices', value: stats.revenue.pendingInvoices, color: 'text-amber-600' }
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <p className="text-xs text-gray-500 mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Plan breakdown */}
      {stats?.planBreakdown && stats.planBreakdown.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Active Tenants by Plan</h2>
          <div className="space-y-3">
            {stats.planBreakdown.map(plan => {
              const maxCount = Math.max(...stats.planBreakdown.map(p => Number(p.tenantCount) || 0), 1);
              const count = Number(plan.tenantCount) || 0;
              const pct = Math.round((count / maxCount) * 100);
              return (
                <div key={plan.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 font-medium">{plan.name}</span>
                    <span className="text-gray-500">{count} tenant{count !== 1 ? 's' : ''} · {formatCurrency(plan.priceMonthly)}/mo</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tenants with billing issues */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Tenants Requiring Attention</h2>
          <p className="text-xs text-gray-500 mt-0.5">Tenants with past_due, suspended, or near trial expiry</p>
        </div>
        <div className="divide-y divide-gray-50">
          {tenants
            .filter(t => ['past_due', 'suspended'].includes(t.subscriptionStatus) ||
              (t.subscriptionStatus === 'trialing' && t.trialEndsAt && new Date(t.trialEndsAt) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)))
            .map(tenant => (
              <div key={tenant.id} className="px-5 py-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 text-sm">{tenant.businessName}</p>
                  <p className="text-xs text-gray-500">{tenant.contactEmail}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[tenant.subscriptionStatus] || 'bg-gray-100 text-gray-600'}`}>
                    {tenant.subscriptionStatus}
                  </span>
                  {tenant.subscriptionStatus === 'trialing' && (
                    <span className="text-xs text-amber-600">
                      Trial ends {formatDate(tenant.trialEndsAt)}
                    </span>
                  )}
                  <button
                    onClick={() => navigate(`/superadmin/tenants/${tenant.id}`)}
                    className="text-purple-600 hover:text-purple-800 p-1 rounded"
                    title="View tenant"
                  >
                    <EyeIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          {tenants.filter(t => ['past_due', 'suspended'].includes(t.subscriptionStatus) ||
            (t.subscriptionStatus === 'trialing' && t.trialEndsAt && new Date(t.trialEndsAt) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))).length === 0 && (
            <div className="px-5 py-8 text-center text-gray-400 text-sm">
              All tenants are in good standing 🎉
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
